/**
 * atomberg-sticky-atc.js
 *
 * Layered on top of Horizon's native <sticky-add-to-cart> web component.
 * Three responsibilities:
 *
 *   1. Decide when the bar shows: whenever the main Add to cart button and,
 *      when the product has one, the Join Live Demo button are both off screen
 *      — still below the fold on page load, or scrolled up behind the sticky
 *      header — and it stays over the footer, which is padded by the bar's
 *      measured height (`--atomberg-sticky-atc-bar-space`). Horizon's own
 *      observer only shows the bar after the buy-buttons block is scrolled past
 *      (so it covers a still visible Join Live Demo button) and hides it at the
 *      footer, so the CSS keys off `data-atomberg-visible` instead of Horizon's
 *      `data-stuck`.
 *
 *   2. Toggle a `data-in-cart="true|false"` attribute on the sticky element
 *      based on whether the *currently selected variant* is in the cart.
 *      CSS uses that attribute to reveal/hide the "View Cart" button.
 *
 *   3. Wire the "View Cart" button to open Horizon's cart drawer by
 *      programmatically clicking the header cart trigger.
 *
 * Sources of truth for the in-cart check:
 *   - On load:         fetch('/cart.js') against data-current-variant-id
 *   - On cart update:  re-fetch (covers add, qty change, removal)
 *   - On variant flip: re-fetch (the new variant may already be in the cart)
 */
(function () {
  'use strict';

  /** @type {any} */
  var w = window;
  if (w._atombergStickyAtcInit) return;
  w._atombergStickyAtcInit = true;

  var WATCHED_BUTTONS =
    '.product-information product-form-component [ref="addToCartButton"], .product-information [data-join-live-demo]';

  /**
   * @param {HTMLElement} root
   */
  function updateVisibility(root) {
    var section = root.closest('.shopify-section');
    var headerHeight =
      parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    var found = false;
    var anyOnScreen = false;
    if (section) {
      section.querySelectorAll(WATCHED_BUTTONS).forEach(function (button) {
        var rect = button.getBoundingClientRect();
        // A hidden Join Live Demo button (height 0) must not count as off screen.
        if (!rect.height) return;
        found = true;
        if (rect.bottom > headerHeight && rect.top < window.innerHeight) anyOnScreen = true;
      });
    }
    root.setAttribute('data-atomberg-visible', found && !anyOnScreen ? 'true' : 'false');
  }

  /**
   * @param {string} variantId
   * @returns {Promise<boolean>}
   */
  function isVariantInCart(variantId) {
    if (!variantId) return Promise.resolve(false);
    // fresh — a cart:update may have just changed the very line we check.
    var req = w.AtombergCartState
      ? w.AtombergCartState.getCart({ fresh: true })
      : fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) {
          return r.json();
        });
    return req
      .then(function (/** @type {any} */ cart) {
        if (!cart || !cart.items) return false;
        var vid = String(variantId);
        for (var i = 0; i < cart.items.length; i++) {
          if (String(cart.items[i].variant_id) === vid) return true;
        }
        return false;
      })
      .catch(function () {
        return false;
      });
  }

  /**
   * @param {HTMLElement} root
   */
  function syncFromServer(root) {
    var variantId = root.getAttribute('data-current-variant-id') || '';
    if (!variantId) {
      root.setAttribute('data-in-cart', 'false');
      return;
    }
    isVariantInCart(variantId).then(function (inCart) {
      root.setAttribute('data-in-cart', inCart ? 'true' : 'false');
    });
  }

  /**
   * @param {HTMLElement} root
   */
  function bind(root) {
    syncFromServer(root);

    var frame = 0;
    function scheduleVisibility() {
      if (frame) return;
      frame = requestAnimationFrame(function () {
        frame = 0;
        updateVisibility(root);
      });
    }
    window.addEventListener('scroll', scheduleVisibility, { passive: true });
    window.addEventListener('resize', scheduleVisibility);
    updateVisibility(root);

    var bar = root.querySelector('.atomberg-sticky-atc__bar');
    if (bar instanceof HTMLElement) {
      var barEl = bar;
      // The footer sits outside <sticky-add-to-cart>, so the bar's real footprint is published on body.
      new ResizeObserver(function () {
        var bottom = parseFloat(getComputedStyle(barEl).bottom) || 0;
        document.body.style.setProperty(
          '--atomberg-sticky-atc-bar-space',
          barEl.offsetHeight + bottom + 'px'
        );
      }).observe(barEl, { box: 'border-box' });
    }

    document.addEventListener('cart:update', function () {
      syncFromServer(root);
    });

    var section = root.closest('.shopify-section');
    if (section) {
      section.addEventListener('variant:update', function () {
        scheduleVisibility();
        // Horizon morphs the bar and updates data-current-variant-id in this same task; check after.
        setTimeout(function () {
          syncFromServer(root);
        }, 0);
      });
    }

    root.addEventListener('click', function (e) {
      var target = e.target;
      if (!(target instanceof Element)) return;
      var btn = target.closest('[data-atomberg-view-cart-btn]');
      if (!btn) return;
      e.preventDefault();
      var trigger = document.querySelector('[data-testid="cart-drawer-trigger"]');
      if (trigger instanceof HTMLElement) trigger.click();
    });
  }

  function initAll() {
    document.querySelectorAll('[data-atomberg-sticky-atc]').forEach(function (el) {
      var root = /** @type {HTMLElement} */ (el);
      /** @type {any} */
      var anyRoot = root;
      if (anyRoot._atombergStickyAtcBound) return;
      anyRoot._atombergStickyAtcBound = true;
      bind(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
